import React, { useState, useRef } from 'react';
import { Columns, Eye, EyeOff, X } from 'lucide-react';
import { VisibilityState } from '@tanstack/react-table';
import { PortalPopover } from '../ui/PortalPopover';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/Checkbox';

interface ColumnVisibilityMenuProps {
  columns: Array<{ key: string; name: string; isSystem?: boolean }>;
  visibility: VisibilityState;
  onChange: (visibility: VisibilityState) => void;
}

export const ColumnVisibilityMenu: React.FC<ColumnVisibilityMenuProps> = ({
  columns,
  visibility,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const toggleColumn = (key: string) => {
    const isCurrentlyVisible = visibility[key] !== false;
    onChange({
      ...visibility,
      [key]: !isCurrentlyVisible,
    });
  };

  const showAll = () => {
    const next: VisibilityState = {};
    columns.forEach((c) => {
      next[c.key] = true;
    });
    onChange(next);
  };

  const hideAll = () => {
    const next: VisibilityState = {};
    columns.forEach((c) => {
      if (!c.isSystem) {
        next[c.key] = false;
      }
    });
    onChange(next);
  };

  return (
    <div className="relative inline-block">
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="xs"
        onClick={() => setOpen(!open)}
        icon={<Columns size={13} />}
      >
        <span>Columns</span>
      </Button>

      <PortalPopover
        isOpen={open}
        onClose={() => setOpen(false)}
        triggerRef={triggerRef}
        align="left"
        className="w-64 max-w-xs bg-cf-surface border border-cf-border rounded-lg shadow-2xl p-3 font-sans"
      >
        <div className="flex items-center justify-between pb-2 border-b border-cf-border mb-2.5">
          <span className="text-xs font-semibold text-cf-textPrimary">Visible Columns</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => setOpen(false)}
            className="text-cf-textSecondary hover:text-cf-textPrimary"
          >
            <X size={13} />
          </Button>
        </div>

        <div className="space-y-1.5 max-h-56 overflow-y-auto">
          {columns.map((col) => {
            const isVisible = visibility[col.key] !== false;
            return (
              <label
                key={col.key}
                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-cf-surfaceHover text-xs cursor-pointer select-none"
              >
                <span className="text-cf-textPrimary truncate">{col.name}</span>
                <Checkbox
                  checked={isVisible}
                  onChange={() => toggleColumn(col.key)}
                />
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-2.5 mt-2.5 border-t border-cf-border text-xs">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={showAll}
            icon={<Eye size={12} />}
            className="text-cf-orange hover:text-cf-orangeHover"
          >
            Show All
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={hideAll}
            icon={<EyeOff size={12} />}
            className="text-cf-textMuted hover:text-cf-textSecondary"
          >
            Hide All
          </Button>
        </div>
      </PortalPopover>
    </div>
  );
};
